import { AlertTriangle } from 'react-feather';
import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import omit from 'lodash/omit';

import { GetParticipantsQuery as GetParticipants, ParticipantFilters, Permission } from '__generated__/graphql';

import { ExportsContext } from '../../Exports/ExportsProvider';
import { assetUrl, encodeUrl, isEmpty, isGraphQLValidationError } from '../../../common/helpers';
import AddContactForm from './AddContactForm';
import ContactFilters, { ContactFilterParams, ParticipantStats } from './ContactFilters';
import ExportContactsForm from '../ExportContactsForm';
import GetParticipantsQuery from './GetParticipantsQuery';
import NoEventsCard from '../NoEventsCard';
import ProtectedSection from '../../Common/ProtectedSection';
import UI from '../../../common/UI';
import config from '../../config';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useUrlState from '../../../common/useUrlState';

type Participant = GetParticipants['project']['participants']['data'][0];

interface UrlParams extends ContactFilterParams {
  page?: number;
}

const ContactsPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const project = useProject();
  const device = useMediaDevice();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('contacts'));

  const [filtersKey, setFiltersKey] = useState(0);

  // All filters are stored in the URL so that they can easily be shared
  const [{ page, ...filters }, setUrlState] = useUrlState<UrlParams>({
    parse: (rawParams, { string, integer, array }) => ({
      event: string(rawParams.event),
      search: string(rawParams.search),
      locales: array(rawParams.locales, string),
      mail_opt_ins: array(rawParams.mail_opt_ins, string),
      received_messages: array(rawParams.received_messages, string),
      will_receive_messages: array(rawParams.will_receive_messages, string),
      page: integer(rawParams.page, 1),
    }),
    onChange: () => {
      // Rerender the filters when user clicks on 'Contacts' in the nav bar.
      setFiltersKey((key) => key + 1);
    },
  });

  // State that contains all filter stats
  const [filterStats, setFilterStats] = useState<ParticipantStats>();

  const contactsCount = filterStats?.participants || 0;
  const enableExport = contactsCount > 0 && contactsCount <= config.maxExportSize.contacts;

  /** Replaces filters in the URL */
  const handleFiltersChange = (update: (filters: ContactFilterParams) => ContactFilterParams) => {
    setUrlState(({ page, ...urlState }) => update({ ...urlState, ...filters }), true);
  };

  const handleStatsChange = (stats: ParticipantStats) => {
    setFilterStats(stats);
  };

  /** Params object that can be sent to the server */
  const participantFilters: ParticipantFilters = {
    ...omit(filters, 'event'),
    events: filters.event ? [filters.event] : undefined,
  };

  // Fetches the filtered participants
  const { data, loading } = useQuery(GetParticipantsQuery, {
    variables: {
      ...participantFilters,
      project_id: project.id,
      page,
      per_page: 20,
    },
    onError: (error) => {
      if (isGraphQLValidationError(error)) {
        // Recent event no longer exists, reset it.
        recentEvent.reset();
      }
    },
  });

  const events = data?.project.events;
  const participants = data?.project.participants;

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((filter) => !isEmpty(filter));

  const [openAddContactModal, closeAddContactModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeAddContactModal}>
      <AddContactForm onSuccess={(id) => history.push(`/${project.id}/participants/${id}`)} />
    </UI.Modal>
  ), []);

  const { openExportsModal } = useContext(ExportsContext);

  const [openExportContactsModal, closeExportContactsModal] = useModal(() => (
    <UI.Modal maxWidth={800} close={closeExportContactsModal}>
      <ExportContactsForm
        filters={participantFilters}
        contactsCount={contactsCount}
        onSuccess={() => {
          closeExportContactsModal();
          openExportsModal('recent_exports');
        }}
      />
    </UI.Modal>
  ), [filters, contactsCount, openExportsModal]);

  if (!events) {
    return <UI.PageLoader />;
  }

  if (events.total === 0) {
    return <NoEventsCard />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.GridContainer sc={{ columns: ['2fr 3fr'], alignVertical: 'center' }}>
          <UI.Div>
            <UI.SectionHeading>
              {t('contacts')}
            </UI.SectionHeading>
          </UI.Div>
          <UI.FlexContainer sc={{ justifyContent: 'end', spaceX: 1 }}>
            <ProtectedSection permissions={[Permission.edit_registrations]}>
              <UI.Button
                onClick={openAddContactModal}
                sc={{ brand: 'secondary' }}
              >
                {t('add_contact')}
              </UI.Button>
            </ProtectedSection>
            {!device.isSmall && (
              <ProtectedSection permissions={[Permission.exports]}>
                <UI.TooltipContainer>
                  {contactsCount > config.maxExportSize.contacts && (
                    <UI.Tooltip sc={{ alignH: 'center', alignV: 'bottom' }}>
                      <UI.Icon>
                        <AlertTriangle />
                      </UI.Icon>
                      {' '}
                      {t('export_too_large')}
                    </UI.Tooltip>
                  )}
                  <UI.Button
                    onClick={() => openExportContactsModal()}
                    disabled={!enableExport}
                  >
                    {t('export')}
                  </UI.Button>
                </UI.TooltipContainer>
              </ProtectedSection>
            )}
          </UI.FlexContainer>
        </UI.GridContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }}>
          <UI.Div>
            <ContactFilters
              filters={filters}
              onChange={handleFiltersChange}
              onStatsChange={handleStatsChange}
              key={filtersKey}
            />
          </UI.Div>

          <UI.Div>
            <UI.GridContainer sc={{ gutter: 1 / 3, loading }}>
              {participants.data.map((participant) => (
                <ContactCard
                  participant={participant}
                  eventId={filters.event}
                  key={participant.id}
                />
              ))}

              {participants.data.length === 0 && (
                <>
                  {hasFilters && (
                    <UI.Card
                      sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                      style={{ textWrap: 'balance' }}
                    >
                      <UI.Div>
                        <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                      </UI.Div>
                      {t('no_contacts_match_filters')}
                      {' '}
                      <UI.Link to={`/${project.id}/participants/contacts`}>{t('reset_filters')}</UI.Link>
                    </UI.Card>
                  )}

                  {!hasFilters && !loading && (
                    <UI.Card
                      sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}
                      style={{ textWrap: 'balance' }}
                    >
                      <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                      <UI.H3 sc={{ mb: 2 }}>{t('no_contacts_yet')}</UI.H3>
                      <UI.Paragraph sc={{ mb: 0 }}>
                        {t('no_contacts_get_started')}
                      </UI.Paragraph>
                    </UI.Card>
                  )}
                </>
              )}
            </UI.GridContainer>

            {(page > 1 || participants.has_more_pages) && (
              <UI.Div sc={{ mt: 4 }}>
                <UI.Pager disableNext={!participants.has_more_pages} scroll />
              </UI.Div>
            )}
          </UI.Div>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Section>
  );
};

const ContactCard = ({
  participant,
  eventId,
}: {
  participant: Participant;
  eventId?: string | null;
},
) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const to = `/${project.id}/participants/${participant.id}?${encodeUrl({ event: eventId })}`;

  return (
    <UI.NavCard to={to}>
      <UI.GridContainer sc={{ columns: ['5fr 1fr 130px', '3fr 4fr 130px'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div style={{ minWidth: 0 }}>
          <UI.H4
            sc={{
              muted: !participant.active,
              deleted: !participant.active,
            }}
          >
            <UI.Truncate>
              {participant.full_name}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        <UI.Div sc={{ color: 'gray.500' }} style={{ minWidth: 0 }}>
          <UI.Truncate>
            {participant.email}
          </UI.Truncate>
        </UI.Div>
        <UI.Div sc={{ small: true, textAlign: 'right' }}>
          <UI.Label>
            {t('n_events', {
              count: participant.events_count,
              value: formatNumber(participant.events_count),
            })}
          </UI.Label>
        </UI.Div>
      </UI.GridContainer>
    </UI.NavCard>
  );
};

export default ContactsPage;
